<script lang="ts" setup>
import { ref, onMounted, inject } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import axiosInstance from '@/plugins/axios';
import { useToast } from 'primevue/usetoast';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useConfirm } from 'primevue/useconfirm';
import { ITranslation } from '@airin/shared-types';


const gF = inject('gF') as any;
const route = useRoute();
const router = useRouter();
const confirm = useConfirm()
const rootTranslation = ref({
    questions: {},
    options: {},
});

const optionIds = ref([]);
const isSaving = ref(false);
const toast = useToast();
const translationID = ref(route.params.translation);
const questionID = ref(route.params.questionID);
const targetLanguage = ref(route.params.translation || 'error');

const translation = ref<ITranslation>({
    questions: {},
    options: {},
});

// Variablen zum Öffnen der Modals und Save der aktuellen Daten
const selectedOptionId = ref(null);
const editLawTextModalVisible = ref(false);
const editQuestionHelpModalVisible = ref(false);
const modalLawText = ref('');
const modalQuestionHelp = ref('');
const hasUnsavedChanges = ref(false);

const getRootTranslation = async () => {
    try {
        if (targetLanguage.value === '670e1e32d4634dd3c3ec7478') {
            const response = await axiosInstance.get(`/translations/670fad078be48456eb53b250`);
            rootTranslation.value = response.data;
        } else {
        const response = await axiosInstance.get(`/translations/670e1e32d4634dd3c3ec7478`);
        rootTranslation.value = response.data;
        }
    } catch (error) {
        console.log(error);
    }
};

const getTranslation = async () => {
    gF.startLoading();
    try {
        const response = await axiosInstance.get(`/translations/${targetLanguage.value}`);
        console.log(response.data);
        translation.value = response.data;
        gF.endLoading();
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Translation could not be loaded: ' + error,
            life: 3000,
        });
        gF.endLoading();
    }
};

const getOptionIds = async () => {
    try {
        const response = await axiosInstance.get(`/questions/${questionID.value}`);
        optionIds.value = response.data.options;
    } catch (error) {
        console.log(error);
    }
};

// Funktion zum Öffnen des LawText-Modals
const openEditLawTextModal = (oId) => {
    selectedOptionId.value = oId;
    modalLawText.value = translation.value.options[oId]?.lawText || '';
    editLawTextModalVisible.value = true;
};

// Funktion zum Öffnen des QuestionHelp-Modals
const openEditQuestionHelpModal = (oId) => {
    selectedOptionId.value = oId;
    modalQuestionHelp.value = translation.value.options[oId]?.info || '';
    editQuestionHelpModalVisible.value = true;
};

// Funktion zum Save der Änderungen in den lokalen Refs
const saveLocalChanges = (type) => {
    console.log('saveLocalChanges');
    console.log(selectedOptionId.value);
    console.log(modalLawText.value);
    console.log(modalQuestionHelp.value);

    if (selectedOptionId.value && type === 'lawText') {
        translation.value.options[selectedOptionId.value].lawText = modalLawText.value.trim() === '<p></p>' ? null : modalLawText.value;
        hasUnsavedChanges.value = true;
    }
    if (selectedOptionId.value && type === 'info') {
        translation.value.options[selectedOptionId.value].info = modalQuestionHelp.value.trim() === '<p></p>' ? null : modalQuestionHelp.value;
        hasUnsavedChanges.value = true;
    }
};

// Warnung bei Verlassen der Seite, falls Änderungen nicht gespeichert wurden
onBeforeRouteLeave((to, from, next) => {
    if (hasUnsavedChanges.value) {
        confirm.require({
            message: 'You have unsaved changes. Are you sure you want to leave without saving?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            rejectProps: {
                label: 'Cancel',
                severity: 'secondary',
                outlined: true
            },
            acceptProps: {
                label: 'Leave'
            },
            accept: () => {
                next();
            },
            reject: () => {
                next(false);
            }
        });
    } else {
        next();
    }
});

const checkForUnsavedChanges = () => {
    hasUnsavedChanges.value = true;
};

const saveTranslation = async () => {
    gF.startLoading();
    isSaving.value = true;
    try {
        const displayedOptions = optionIds.value.reduce((acc, oId) => {
            if (translation.value.options[oId]) {
                acc[oId] = translation.value.options[oId];
            }
            return acc;
        }, {});

        const response = await axiosInstance.put(`/translations/updateOptions/${translationID.value}`, {
            options: displayedOptions
        });
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Displayed options saved successfully',
            life: 3000
        });
        hasUnsavedChanges.value = false;
        gF.endLoading();
        isSaving.value = false;
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Options could not be saved: ' + error,
            life: 3000
        });
        gF.endLoading();
        isSaving.value = false;
    }
};

onMounted(async () => {
    await getRootTranslation();
    await getTranslation();
    await getOptionIds();
});
</script>

<template>
    <div>
        <div class="row">
            <div class="row col-12 justify-content-center">
                <div class="col text-center">
                    <Button class="w-30 p-button-tuev" label="Save" @click="saveTranslation()" iconPos="left" :loading="isSaving">
                        <template #icon>
                            <font-awesome-icon :icon="['fas', 'save']" />
                        </template>
                    </Button>
                </div>
            </div>
            <Card class="col-12 m-2" v-for="oId in optionIds" :key="oId">
                <template #content>
                    <div class="row gap-2">
                        <div class="col-3">
                            <label for="setting">Default</label>
                        </div>
                        <div class="col-8">
                            {{ rootTranslation?.options[oId] && rootTranslation?.options[oId]?.title }}
                        </div>
                        <div class="col-3">
                            <label for="setting">{{ translation?.languageCode }}</label>
                        </div>
                        <div class="col-8">
                            <TextArea
                                autoResize
                                v-model="translation.options[oId].title"
                                class="w-100"
                                @input="checkForUnsavedChanges"
                            />
                        </div>
                        <div class="row mt-2 justify-content-end">
                            <!-- Button zum Öffnen des LawText-Modals -->
                            <Button
                                v-if="rootTranslation?.options[oId]?.lawText || translation?.options[oId]?.lawText"
                                :class="{
                                    'col-1': true,
                                    'p-button-outlined': !rootTranslation?.options[oId]?.lawText && !translation?.options[oId]?.lawText,
                                    'p-button-warning': rootTranslation?.options[oId]?.lawText && !translation?.options[oId]?.lawText,
                                    'p-button-tuev': rootTranslation?.options[oId]?.lawText && translation?.options[oId]?.lawText
                                }"
                                class="mr-2"
                                @click="openEditLawTextModal(oId)"
                            >
                                <template #icon>
                                    <font-awesome-icon :icon="['fa', 'section']" />
                                </template>
                            </Button>

                            <!-- Button zum Öffnen des QuestionHelp-Modals -->
                            <Button
                                v-if="rootTranslation?.options[oId]?.info || translation?.options[oId]?.info"
                                :class="{
                                    'col-1': true,
                                    'p-button-outlined': !rootTranslation?.options[oId]?.info && !translation?.options[oId]?.info,
                                    'p-button-warning': rootTranslation?.options[oId]?.info && !translation?.options[oId]?.info,
                                    'p-button-tuev': rootTranslation?.options[oId]?.info && translation?.options[oId]?.info
                                }"
                                @click="openEditQuestionHelpModal(oId)"
                            >
                                <template #icon>
                                    <font-awesome-icon :icon="['fa', 'info']" />
                                </template>
                            </Button>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Modal zum Bearbeiten des LawText -->
        <Dialog
            v-model:visible="editLawTextModalVisible"
            maximizable
            modal
            header="Edit Law Text"
            :style="{ width: '70rem' }"
            :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        >
            <div class="m-0">
                <div class="row gap-2">
                    <div class="col-3">
                        <label for="setting">Root Language</label>
                    </div>
                    <div class="col-8" v-html="rootTranslation?.options[selectedOptionId] && rootTranslation?.options[selectedOptionId]?.lawText">
                    </div>
                    <div class="col-3">
                        <label for="setting">{{ translation?.languageCode }}</label>
                    </div>
                    <div class="col-8">
                        <LimitedEditor v-model="modalLawText" editorStyle="height: 320px"  class="w-100" />
                    </div>
                </div>
                <div class="row justify-content-end m-3">
                    <div class="col-1">
                        <Button class="w-100" severity="primary" @click="() => { saveLocalChanges('lawText'); editLawTextModalVisible = false; }">Close</Button>
                    </div>
                </div>
            </div>
        </Dialog>

        <!-- Modal zum Bearbeiten des QuestionHelp -->
        <Dialog
            v-model:visible="editQuestionHelpModalVisible"
            maximizable
            modal
            header="Edit Question Help"
            :style="{ width: '70rem' }"
            :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        >
            <div class="m-0">
                <div class="row gap-2">
                    <div class="col-3">
                        <label for="setting">Root Language</label>
                    </div>
                    <div class="col-8" v-html="rootTranslation.options[selectedOptionId] && rootTranslation.options[selectedOptionId].info">
                    </div>
                    <div class="col-3">
                        <label for="setting">{{ translation.languageCode }}</label>
                    </div>
                    <div class="col-8">
                        <LimitedEditor v-model="modalQuestionHelp" editorStyle="height: 320px"  class="w-100" />
                    </div>
                </div>
                <div class="row justify-content-end m-3">
                    <div class="col-1">
                        <Button class="w-100" severity="primary" @click="() => { saveLocalChanges('info'); editQuestionHelpModalVisible = false; }">Close</Button>
                    </div>
                </div>
            </div>
        </Dialog>
    </div>
</template>

<style scoped>
.p-button-warning {
    background-color: #f0ad4e !important;
    border-color: #eea236 !important;
    color: #fff !important;
}

.p-button-tuev {
    background-color: #00dce6 !important;
    border-color: #00dce6 !important;
    color: #fff !important;
}

.p-button-outlined {
    background-color: transparent !important;
    border-color: #00dce6 !important;
    color: #00dce6 !important;
}

</style>